import { createContext, useContext, useEffect, useState } from 'react'
import { useParams, useNavigate, Outlet } from 'react-router-dom'
import {
  Edit2,
  Trash2,
  Power,
  Settings,
} from 'lucide-react'
import { toast } from 'sonner'
import { ruleService } from '../../../services/ruleService'
import {
  Rule,
  REQUEST_TYPE_LABELS,
  WORK_CENTER_LABELS,
  RULE_CATEGORY_LABELS,
} from '../../../types'
import { ConfirmDialog, useConfirmDialog } from '../../../components/ui/ConfirmDialog'
import { RuleFormModal } from '../../../components/rules/RuleFormModal'
import { DetailLayout } from '../../../components/ui/DetailLayout'
import { TabItem } from '../../../components/ui/DetailTabs'

interface RuleContextType {
  rule: Rule | null
  isLoading: boolean
  handleToggleActive: () => Promise<void>
  fetchRule: () => Promise<void>
}

const RuleContext = createContext<RuleContextType | null>(null)

export function useRuleContext() {
  const context = useContext(RuleContext)
  if (!context) {
    throw new Error('useRuleContext must be used within RuleDetailLayout')
  }
  return context
}

export function RuleDetailLayout() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()

  const [rule, setRule] = useState<Rule | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [showEditModal, setShowEditModal] = useState(false)

  const confirmDialog = useConfirmDialog()

  useEffect(() => {
    if (id) {
      fetchRule()
    }
  }, [id])

  const fetchRule = async () => {
    try {
      const data = await ruleService.getById(id!)
      setRule(data)
    } catch (error) {
      toast.error('Error al cargar regla')
      navigate('/configuration/rules')
    } finally {
      setIsLoading(false)
    }
  }

  const handleToggleActive = async () => {
    if (!rule) return
    try {
      const updated = await ruleService.toggleActive(rule.id)
      toast.success(`Regla ${updated.isActive ? 'activada' : 'desactivada'}`)
      setRule(updated)
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Error al cambiar estado')
    }
  }

  const handleDelete = async () => {
    if (!rule) return

    const confirmed = await confirmDialog.confirm({
      title: 'Eliminar regla',
      message: `¿Estás seguro de eliminar la regla "${rule.name}"? Esta acción no se puede deshacer.`,
      confirmText: 'Eliminar',
      cancelText: 'Cancelar',
      variant: 'danger',
    })

    if (!confirmed) return

    try {
      await ruleService.delete(rule.id)
      toast.success('Regla eliminada')
      navigate('/configuration/rules')
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Error al eliminar')
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary-200 border-t-primary-600"></div>
      </div>
    )
  }

  if (!rule) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500 dark:text-gray-400">Regla no encontrada</p>
      </div>
    )
  }

  const tabs: TabItem[] = [
    { label: 'Configuración', path: '', icon: Settings, end: true },
  ]

  const contextValue: RuleContextType = {
    rule,
    isLoading,
    handleToggleActive,
    fetchRule,
  }

  return (
    <RuleContext.Provider value={contextValue}>
      <DetailLayout
        title={rule.name}
        subtitle={RULE_CATEGORY_LABELS[rule.category]}
        backPath="/configuration/rules"
        basePath={`/configuration/rules/${id}`}
        tabs={tabs}
        avatar={
          <div className="h-12 w-12 rounded-lg bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
            <Settings className="h-6 w-6 text-primary-600" />
          </div>
        }
        badges={
          <>
            <span className="font-mono text-sm bg-gray-100 dark:bg-slate-700 px-2 py-1 rounded">
              {rule.code}
            </span>
            <span className={`badge ${rule.isActive ? 'badge-success' : 'badge-gray'}`}>
              {rule.isActive ? 'Activa' : 'Inactiva'}
            </span>
            {rule.requestType && (
              <span className="badge badge-primary">
                {REQUEST_TYPE_LABELS[rule.requestType]}
              </span>
            )}
            {rule.workCenter && (
              <span className="badge badge-info">
                {WORK_CENTER_LABELS[rule.workCenter]}
              </span>
            )}
            <button
              onClick={handleToggleActive}
              className={`btn btn-sm ${rule.isActive ? 'btn-secondary' : 'bg-success-50 text-success-700 hover:bg-success-100'}`}
            >
              <Power className="h-3.5 w-3.5 mr-1" />
              {rule.isActive ? 'Desactivar' : 'Activar'}
            </button>
          </>
        }
        actions={
          <>
            <button
              onClick={() => setShowEditModal(true)}
              className="btn btn-primary flex items-center gap-2"
            >
              <Edit2 className="h-4 w-4" />
              <span className="hidden sm:inline">Editar</span>
            </button>
            <button
              onClick={handleDelete}
              className="btn btn-danger flex items-center gap-2"
            >
              <Trash2 className="h-4 w-4" />
              <span className="hidden sm:inline">Eliminar</span>
            </button>
          </>
        }
      >
        <Outlet />
      </DetailLayout>

      {/* Edit Modal */}
      {showEditModal && (
        <RuleFormModal
          rule={rule}
          onClose={() => setShowEditModal(false)}
          onSuccess={() => {
            setShowEditModal(false)
            fetchRule()
          }}
        />
      )}

      {/* Confirm Dialog */}
      <ConfirmDialog {...confirmDialog.dialogProps} />
    </RuleContext.Provider>
  )
}
